import { inject, ref, type DefineComponent, type Ref } from 'vue';
import TabsRoot from './TabsRoot.vue';
import TabPanelSfc from './TabPanel.vue';
import TabSelectorsSfc from './TabSelectors.vue';
import { tabsPanelIdKey, tabsRegistryKey } from './internal';
import type { TabEntry, TabsRegistry } from './useTabsRegistry';

/**
 * Creates a generically-typed tab system. Call once (e.g. in a consumer's
 * `<script setup>`). The runtime wiring is the shared module-level key +
 * provide/inject (hierarchy isolates trees); this factory's job is purely to
 * pin the imported generic SFCs to one shared `T`, so every `TabPanel` /
 * `TabSelectors` in the consumer's tree carries the same metadata type.
 */
export function defineTabs<T>() {
  // TabsRoot is type-erased (provides registry<unknown>); returned as-is.
  // TabPanel/TabSelectors are generic SFCs; pin their T to the factory's T.
  const TabPanel = TabPanelSfc as unknown as DefineComponent<{
    id: string;
    name: string;
    info: T;
  }>;
  const TabSelectors = TabSelectorsSfc as unknown as new () => {
    $props: Record<string, never>;
    $slots: { default: (props: { tabs: TabEntry<T>[] }) => unknown };
  };

  /** Raw registry access (entries / selectedId / select). Throws outside TabsRoot. */
  function useTabs(): TabsRegistry<T> {
    const registry = inject(tabsRegistryKey);
    if (!registry) throw new Error('[nx-ui] useTabs() must be called inside TabsRoot');
    return registry as TabsRegistry<T>;
  }

  /** The nearest TabPanel's id (for descendants); ref('') + dev-warn outside a panel. */
  function usePanelId(): Ref<string> {
    const injected = inject(tabsPanelIdKey, null);
    if (!injected) {
      if (import.meta.env?.DEV) {
        console.warn('[nx-ui] usePanelId() was called outside a TabPanel.');
      }
      return ref('');
    }
    return injected;
  }

  return { TabsRoot, TabPanel, TabSelectors, useTabs, usePanelId };
}
